import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAppSelector } from '@/store/hooks';
import {
  selectFeatureFlags,
  selectApexEnabled,
  selectCanAccessPlanner,
  selectUser,
} from '@/store/features/userinfo/userinfoSlice';
import { isFirstBillingSetup, shouldRedirectToBillingSetup } from '@/pages/stripe/billing-status';
import { RouteLoadingFallback } from './route-loading-fallback';
import {
  hasInitialAppLoadState,
  type InitialAppLoadDestination,
  markInitialAppLoadState,
} from '@/routes/initial-app-load-state';

/**
 * Renders the default redirect when user lands on /home.
 * - If the firm admin still needs billing setup → redirect to stripe-config.
 * - If apex_enabled is true → redirect to dashboard.
 * - If apex_enabled is false and planner access is enabled → redirect to planning/guided-mode.
 * - If neither apex nor planner access is enabled → redirect to clients.
 * Shows the branded page loader while profile routing data is being fetched.
 */
const HomeDefaultRedirect: React.FC = () => {
  const location = useLocation();
  const featureFlags = useAppSelector(selectFeatureFlags);
  const apexEnabled = useAppSelector(selectApexEnabled);
  const canAccessPlanner = useAppSelector(selectCanAccessPlanner);
  const user = useAppSelector(selectUser);
  const isInitialAppLoad = hasInitialAppLoadState(location.state);
  const getNextInitialAppLoadState = (destination: InitialAppLoadDestination) =>
    isInitialAppLoad
      ? markInitialAppLoadState(location.state, undefined, { destination })
      : undefined;
  const renderDestinationRedirect = (to: string, destination: InitialAppLoadDestination) => {
    const redirect = <Navigate to={to} replace state={getNextInitialAppLoadState(destination)} />;

    return isInitialAppLoad ? (
      <>
        <RouteLoadingFallback />
        {redirect}
      </>
    ) : (
      redirect
    );
  };

  const isLoading =
    featureFlags.loading ||
    ((featureFlags.profile === undefined || featureFlags.stripeInfo === undefined) &&
      !featureFlags.error);

  if (isLoading) {
    return <RouteLoadingFallback />;
  }

  if (featureFlags.error) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <p style={{ color: '#666', marginBottom: 8 }}>
          Unable to load your profile. Please refresh the page or try again later.
        </p>
      </div>
    );
  }

  if (shouldRedirectToBillingSetup(user, featureFlags.stripeInfo)) {
    return (
      <Navigate
        to={
          isFirstBillingSetup(featureFlags.stripeInfo)
            ? '/stripe-config'
            : '/subscriptions?manage=1'
        }
        replace
      />
    );
  }

  // apex_enabled === true → dashboard
  if (apexEnabled === true) {
    return renderDestinationRedirect('dashboard', 'dashboard');
  }

  // Only send to planning/guided-mode if user has planner access; otherwise clients.
  if (canAccessPlanner) {
    return renderDestinationRedirect('planning/guided-mode-v2', 'planner');
  }

  return renderDestinationRedirect('clients', 'clients');
};

export default HomeDefaultRedirect;
